/**
 * Touren: aufzeichnen, wiedersehen, weitergeben.
 *
 * Drei Dinge in einer Spalte - der Aufnahmeknopf mit den Zahlen, die daran
 * hängen, die Liste des Aufgezeichneten und der Weg hinaus als GPX. Die Spur
 * wird beim Aufzeichnen mitgezeichnet; wer stehen bleibt, sieht das auch,
 * weil die Linie dann nicht wächst.
 */
import { createRecorder, statistik, alsGpx, alsGeoJson, ausGpx, coords, tourName, ARTEN } from '../data/tracks.js';
import { strecke, dauer } from '../data/navigation.js';
import { toast } from './toast.js';

const QUELLE = 'tour';
const QUELLE_ENDEN = 'tour-enden';
const LEER = { type: 'FeatureCollection', features: [] };

const hhmm = (t) => new Date(t).toLocaleString('de-CH', {
  weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
});

/**
 * Laufende Uhr: 00:42, 12:05, 1:05:30.
 *
 * `dauer()` aus der Zielfuehrung rundet auf Minuten - bei einer laufenden
 * Aufzeichnung steht dann minutenlang "0 Min", und es sieht aus, als tue sich
 * nichts.
 */
function uhr(sekunden) {
  const s = Math.max(0, Math.round(sekunden));
  const zwei = (n) => String(n).padStart(2, '0');
  const m = Math.floor(s / 60);
  if (m < 60) return `${zwei(m)}:${zwei(s % 60)}`;
  return `${Math.floor(m / 60)}:${zwei(m % 60)}:${zwei(s % 60)}`;
}

/** Zahlen einer Tour in einer Zeile: 12,4 km · 2 h 05 · ↑ 940 m */
function zahlen(stats) {
  return [
    strecke(stats.meter),
    stats.sekunden > 60 ? dauer(stats.sekunden) : null,
    stats.aufstieg > 20 ? `↑ ${stats.aufstieg} m` : null
  ].filter(Boolean).join(' · ');
}

/** Dasselbe ohne die Strecke - die steht daneben schon gross. */
function nebenzahlen(stats, extra = []) {
  return [uhr(stats.sekunden), stats.aufstieg ? `↑ ${stats.aufstieg} m` : null, ...extra]
    .filter(Boolean).join(' · ');
}

function herunterladen(name, text, typ) {
  const blob = new Blob([text], { type: typ });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  // Erst freigeben, wenn der Browser den Griff hat.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const dateiName = (tour, endung) =>
  `${(tour.name || 'tour').replace(/[^\wÀ-ɏ -]+/g, '').trim().replace(/\s+/g, '-').toLowerCase()}.${endung}`;

export function createTracksPanel({ map, backend, getProfile, onAktiv, onNavigate }) {
  let nodes = null;
  let touren = [];              // nur die Merkmale, ohne Punkte
  let offen = null;             // id der aufgeklappten Tour
  let gezeichnet = null;        // zuletzt gezeichnete Punkte, fuer den Stilwechsel
  let entwurf = null;           // fertig aufgezeichnet, noch nicht gespeichert

  /* --------------------------------------------------------- Kartenebenen */

  function ebenenAnlegen() {
    if (map.getSource(QUELLE)) return;
    map.addSource(QUELLE, { type: 'geojson', data: LEER });
    map.addSource(QUELLE_ENDEN, { type: 'geojson', data: LEER });
    map.addLayer({
      id: 'tour-casing',
      type: 'line',
      source: QUELLE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FCFCFC', 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 9, 18, 14] }
    });
    map.addLayer({
      id: 'tour-line',
      type: 'line',
      source: QUELLE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // Bengara - dieselbe Farbe, mit der die Karte schon Besonderes
        // auszeichnet, und auf dem Landeskartenbild gut zu sehen.
        'line-color': '#B00020',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 14, 4, 18, 6]
      }
    });
    map.addLayer({
      id: 'tour-enden-punkt',
      type: 'circle',
      source: QUELLE_ENDEN,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3.5, 14, 6],
        'circle-color': ['case', ['==', ['get', 'rolle'], 'start'], '#FCFCFC', '#B00020'],
        'circle-stroke-color': '#B00020',
        'circle-stroke-width': 2
      }
    });
  }

  function zeichnen(punkte, { zoomen = false } = {}) {
    gezeichnet = punkte;
    ebenenAnlegen();
    const linie = coords(punkte);
    map.getSource(QUELLE).setData(linie.length > 1
      ? { type: 'Feature', geometry: { type: 'LineString', coordinates: linie }, properties: {} }
      : LEER);
    map.getSource(QUELLE_ENDEN).setData({
      type: 'FeatureCollection',
      features: linie.length
        ? [
          { type: 'Feature', geometry: { type: 'Point', coordinates: linie[0] }, properties: { rolle: 'start' } },
          ...(linie.length > 1
            ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: linie[linie.length - 1] }, properties: { rolle: 'ende' } }]
            : [])
        ]
        : []
    });
    if (zoomen && linie.length > 1) {
      const lngs = linie.map((c) => c[0]);
      const lats = linie.map((c) => c[1]);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 70, duration: 700 }
      );
    }
  }

  function loeschenAufKarte() {
    gezeichnet = null;
    if (!map.getSource(QUELLE)) return;
    map.getSource(QUELLE).setData(LEER);
    map.getSource(QUELLE_ENDEN).setData(LEER);
  }

  /** setStyle wirft eigene Quellen weg - hier kommen sie zurück. */
  function redraw() {
    if (gezeichnet) zeichnen(gezeichnet);
  }

  /* ------------------------------------------------------- Aufzeichnung */

  let ersterPunkt = false;

  const recorder = createRecorder({
    onUpdate: (z) => {
      if (z.laeuft) {
        zeichnen(z.punkte);
        // Beim ersten Messwert einmal hinfliegen: wer aufzeichnet, will sich
        // auf der Karte sehen und nicht erst die halbe Schweiz wegschieben.
        if (!ersterPunkt && z.punkte.length) {
          ersterPunkt = true;
          const p = z.punkte[0];
          map.easeTo({ center: [p.lng, p.lat], zoom: Math.max(map.getZoom(), 15), duration: 800 });
        }
      }
      zeichneAufnahme(z);
    },
    onFehler: (m) => toast(m)
  });

  function zeichneAufnahme(z) {
    if (!nodes) return;
    const host = nodes.aufnahme;
    host.textContent = '';

    if (entwurf) { entwurfZeigen(host); return; }

    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = `tour__rec${z.laeuft ? ' is-laeuft' : ''}`;
    const punkt = document.createElement('span');
    punkt.className = 'tour__dot';
    const text = document.createElement('span');
    text.textContent = z.laeuft ? 'Aufzeichnung beenden' : 'Aufzeichnen';
    knopf.append(punkt, text);
    knopf.addEventListener('click', () => (z.laeuft ? beenden() : beginnen()));
    host.append(knopf);

    if (!z.laeuft) {
      const hinweis = document.createElement('p');
      hinweis.className = 'hint hint--block';
      hinweis.textContent = 'Der Bildschirm bleibt während der Aufzeichnung an. '
        + 'Sperrt sich das Gerät trotzdem oder wird der Browser geschlossen, bekommt die Spur eine Lücke.';
      host.append(hinweis);
      return;
    }

    const werte = document.createElement('div');
    werte.className = 'tour__live';
    const gross = document.createElement('strong');
    gross.textContent = strecke(z.stats.meter);
    const klein = document.createElement('span');
    klein.textContent = nebenzahlen(z.stats, [
      `${z.stats.punkte} Punkte`,
      z.pausiert ? 'angehalten' : null
    ]);
    werte.append(gross, klein);

    const reihe = document.createElement('div');
    reihe.className = 'tour__row';
    const pause = document.createElement('button');
    pause.type = 'button';
    pause.className = 'tour__btn';
    pause.textContent = z.pausiert ? 'Weiter' : 'Pause';
    pause.addEventListener('click', () => recorder.pause(!z.pausiert));
    reihe.append(pause);

    host.append(werte, reihe);
  }

  function beginnen() {
    if (!navigator.geolocation) { toast('Dieses Gerät kennt keine Ortung.'); return; }
    loeschenAufKarte();
    ersterPunkt = false;
    if (!recorder.start()) toast('Aufzeichnung läuft bereits.');
    else onAktiv?.();
  }

  function beenden() {
    const fertig = recorder.stop();
    if (fertig.punkte.length < 2) {
      toast('Zu wenig Punkte - nichts aufgezeichnet.');
      loeschenAufKarte();
      return;
    }
    entwurf = {
      name: tourName('wandern', fertig.begonnen),
      art: 'wandern',
      begonnen: fertig.begonnen,
      beendet: Date.now(),
      punkte: fertig.punkte,
      stats: fertig.stats
    };
    zeichnen(fertig.punkte, { zoomen: true });
    zeichneAufnahme(recorder.zustand());
  }

  /** Nach dem Beenden: Name und Art, dann speichern - oder verwerfen. */
  function entwurfZeigen(host) {
    const kasten = document.createElement('div');
    kasten.className = 'tour__entwurf';

    const werte = document.createElement('div');
    werte.className = 'tour__live';
    const gross = document.createElement('strong');
    gross.textContent = strecke(entwurf.stats.meter);
    const klein = document.createElement('span');
    klein.textContent = nebenzahlen(entwurf.stats, [`${entwurf.stats.punkte} Punkte`]);
    werte.append(gross, klein);

    const feldName = document.createElement('label');
    feldName.className = 'field';
    const beschriftung = document.createElement('span');
    beschriftung.className = 'field__label';
    beschriftung.textContent = 'Name';
    const eingabe = document.createElement('input');
    eingabe.type = 'text';
    eingabe.value = entwurf.name;
    eingabe.addEventListener('input', () => { entwurf.name = eingabe.value; });
    feldName.append(beschriftung, eingabe);

    const arten = document.createElement('div');
    arten.className = 'segmented segmented--4';
    arten.setAttribute('role', 'radiogroup');
    arten.setAttribute('aria-label', 'Art der Tour');
    for (const a of ARTEN) {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(a.id === entwurf.art));
      b.textContent = a.label;
      b.addEventListener('click', () => {
        // Der vorgeschlagene Name folgt der Art, ein eigener bleibt stehen.
        const vorschlag = tourName(entwurf.art, entwurf.begonnen);
        entwurf.art = a.id;
        if (eingabe.value === vorschlag) {
          entwurf.name = tourName(a.id, entwurf.begonnen);
          eingabe.value = entwurf.name;
        }
        for (const anderer of arten.children) anderer.setAttribute('aria-checked', 'false');
        b.setAttribute('aria-checked', 'true');
      });
      arten.append(b);
    }

    const reihe = document.createElement('div');
    reihe.className = 'tour__row';
    const sichern = document.createElement('button');
    sichern.type = 'button';
    sichern.className = 'tour__btn tour__btn--haupt';
    sichern.textContent = 'Speichern';
    sichern.addEventListener('click', () => speichern(entwurf));
    const weg = document.createElement('button');
    weg.type = 'button';
    weg.className = 'tour__btn';
    weg.textContent = 'Verwerfen';
    weg.addEventListener('click', () => {
      entwurf = null;
      loeschenAufKarte();
      zeichneAufnahme(recorder.zustand());
    });
    reihe.append(sichern, weg);

    kasten.append(werte, feldName, arten, reihe);
    host.append(kasten);
  }

  async function speichern(tour) {
    try {
      const eintrag = await backend.tracks.add(getProfile(), tour);
      entwurf = null;
      touren = [eintrag, ...touren];
      offen = eintrag.id;
      zeichneAufnahme(recorder.zustand());
      render();
      toast(`„${eintrag.name}“ gespeichert.`);
    } catch (e) {
      toast(e.message || 'Speichern fehlgeschlagen.');
    }
  }

  /* -------------------------------------------------------------- Liste */

  async function load() {
    try {
      touren = (await backend.tracks.list(getProfile())) || [];
    } catch {
      touren = [];
      toast('Touren konnten nicht geladen werden.');
    }
    render();
  }

  async function zeigen(tour) {
    if (offen === tour.id) { offen = null; loeschenAufKarte(); render(); return; }
    offen = tour.id;
    render();
    try {
      const voll = await backend.tracks.get(getProfile(), tour.id);
      if (!voll?.punkte?.length || offen !== tour.id) return;
      zeichnen(voll.punkte, { zoomen: true });
      onNavigate?.();
    } catch {
      toast('Tour konnte nicht geladen werden.');
    }
  }

  async function ausgeben(tour, format) {
    try {
      const voll = await backend.tracks.get(getProfile(), tour.id);
      if (!voll?.punkte?.length) { toast('Keine Punkte in dieser Tour.'); return; }
      if (format === 'gpx') herunterladen(dateiName(voll, 'gpx'), alsGpx(voll), 'application/gpx+xml');
      else herunterladen(dateiName(voll, 'geojson'), alsGeoJson(voll), 'application/geo+json');
    } catch {
      toast('Ausgabe fehlgeschlagen.');
    }
  }

  async function entfernen(tour) {
    try {
      await backend.tracks.remove(getProfile(), tour.id);
      touren = touren.filter((t) => t.id !== tour.id);
      if (offen === tour.id) { offen = null; loeschenAufKarte(); }
      render();
    } catch {
      toast('Löschen fehlgeschlagen.');
    }
  }

  function zeile(tour) {
    const row = document.createElement('div');
    row.className = `tour${offen === tour.id ? ' is-offen' : ''}`;

    const haupt = document.createElement('button');
    haupt.type = 'button';
    haupt.className = 'tour__main';
    const name = document.createElement('span');
    name.className = 'tour__name';
    name.textContent = tour.name;
    const meta = document.createElement('span');
    meta.className = 'tour__meta';
    const s = tour.stats || statistik([]);
    meta.textContent = [tour.begonnen ? hhmm(tour.begonnen) : null, zahlen(s)].filter(Boolean).join(' · ');
    haupt.append(name, meta);
    haupt.addEventListener('click', () => zeigen(tour));

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'fav__del';
    del.title = 'Tour löschen';
    del.setAttribute('aria-label', `Tour ${tour.name} löschen`);
    del.textContent = '×';
    del.addEventListener('click', () => entfernen(tour));

    row.append(haupt, del);

    if (offen === tour.id) {
      const aktionen = document.createElement('div');
      aktionen.className = 'tour__aktionen';
      for (const [text, format, titel] of [
        ['GPX', 'gpx', 'Als GPX speichern – liest QGIS direkt'],
        ['GeoJSON', 'geojson', 'Als GeoJSON speichern']
      ]) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tour__btn';
        b.textContent = text;
        b.title = titel;
        b.addEventListener('click', () => ausgeben(tour, format));
        aktionen.append(b);
      }
      row.append(aktionen);
    }
    return row;
  }

  function render() {
    if (!nodes) return;
    nodes.badge.textContent = String(touren.length);
    nodes.badge.hidden = touren.length === 0;
    nodes.liste.textContent = '';
    if (!touren.length) {
      const p = document.createElement('div');
      p.className = 'empty';
      p.textContent = 'Noch nichts aufgezeichnet.';
      nodes.liste.append(p);
      return;
    }
    for (const t of touren) nodes.liste.append(zeile(t));
  }

  /* ------------------------------------------------------------- Import */

  async function gpxLesen(datei) {
    try {
      const tour = ausGpx(await datei.text(), datei.name);
      const eintrag = await backend.tracks.add(getProfile(), tour);
      touren = [eintrag, ...touren].sort((a, b) => (b.begonnen || 0) - (a.begonnen || 0));
      offen = eintrag.id;
      render();
      zeichnen(tour.punkte, { zoomen: true });
      onAktiv?.();
      toast(`„${eintrag.name}“ eingelesen – ${tour.punkte.length} Punkte.`);
    } catch (e) {
      toast(e.message || 'Die Datei liess sich nicht lesen.');
    }
  }

  /**
   * Eine GPX-Datei auf die Karte ziehen.
   *
   * `dragover` muss abgefangen werden, sonst öffnet der Browser die Datei
   * einfach selbst und die Karte ist weg.
   */
  function zumZiehenAnmelden() {
    const feld = map.getContainer();
    const an = (e) => {
      if (![...(e.dataTransfer?.items || [])].some((i) => i.kind === 'file')) return;
      e.preventDefault();
      feld.classList.add('is-dropziel');
    };
    feld.addEventListener('dragover', an);
    feld.addEventListener('dragenter', an);
    feld.addEventListener('dragleave', (e) => {
      if (e.relatedTarget && feld.contains(e.relatedTarget)) return;
      feld.classList.remove('is-dropziel');
    });
    feld.addEventListener('drop', (e) => {
      const datei = e.dataTransfer?.files?.[0];
      if (!datei) return;
      e.preventDefault();
      feld.classList.remove('is-dropziel');
      if (!/\.gpx$/i.test(datei.name)) { toast('Nur GPX-Dateien.'); return; }
      gpxLesen(datei);
    });
  }

  /* --------------------------------------------------------------- Mount */

  function mount(n) {
    nodes = n;
    zeichneAufnahme(recorder.zustand());
    n.importInput?.addEventListener('change', () => {
      const datei = n.importInput.files?.[0];
      if (datei) gpxLesen(datei);
      n.importInput.value = '';
    });
    n.importBtn?.addEventListener('click', () => n.importInput?.click());
    zumZiehenAnmelden();
  }

  return { mount, load, render, redraw, laeuft: () => recorder.laeuft() };
}
